import React, { useMemo, useState } from 'react';

import { Utils } from '@adobe/aem-react-editable-components';
import { MapTo } from '@adobe/aem-spa-component-mapping';
import { useMutation } from '@apollo/client';
import { DELETE_COMPANY, UPDATE_INSTALLER_CONSENT_MUTATION } from 'Queries/Mutations';
import { createPatchEinwilligungByInstallerIdData } from 'Queries/QueriesHelper';
import { useFormik } from 'formik';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CheckboxRow } from 'components/CheckboxRow/CheckboxRow';
import { InputField } from 'components/FormHelper/InputField';
import { SubmitPopup } from 'components/FormHelper/SubmitPopup';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { RenderAemComponent } from 'components/RenderAemComponent/RenderAemComponent';

import { getMyDataPath } from 'utils/UrlUtil';
import { isFachinteressent } from 'utils/InstallerUtils';

import { YDebDeleteCompanyFormSchema } from './YDebDeleteCompanyFormSchema';

import './DebDeleteCompanyForm.sass';

export const DebDeleteCompanyForm = () => {
  const { installer, refetchInstaller } = useCurrentInstaller();
  const isEditor = Utils.isInEditor();
  const [deleteCompanyMutation] = useMutation(DELETE_COMPANY);
  const [updateInstallerConsentMutation] = useMutation(UPDATE_INSTALLER_CONSENT_MUTATION);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [response, setResponse] = useState('');

  const submitForm = async (values) => {
    setIsSubmitting(true);
    try {
      const responseData = await updateInstallerConsentMutation({
        variables: createPatchEinwilligungByInstallerIdData(false, false),
      });

      if (responseData.errors) {
        throw new Error(responseData.errors);
      }

      if (responseData.data.updateInstallerConsent.status !== 200) {
        throw new Error(
          'Mutation error with status: ',
          responseData.data.updateInstallerConsent.status
        );
      }
    } catch (e) {
      console.error(e);
    }
    deleteCompanyMutation({
      variables: {
        deleteCompanyInput: {
          baseDataEntries: [
            { first: 'Email', second: installer.email },
            { first: 'Eintragungsnummer', second: installer.id },
          ],
          deleteReason: values.deleteReason || '-',
          deletedFromDate: values.deleteDate || '-',
        },
      },
    })
      .then(({ data }) => {
        setResponse(data.deleteCompany.status);
        setIsSubmitting(false);
        setOpenPopup(true);
        refetchInstaller();
      })
      .catch(() => {
        setResponse('ERROR');
        setOpenPopup(true);
        setIsSubmitting(false);
      });
  };
  const formik = useFormik({
    initialValues: {
      deletedCheckbox: false,
      deleteDate: '',
      deleteReason: '',
    },
    validationSchema: YDebDeleteCompanyFormSchema,
    enableReinitialize: true,
    onSubmit: submitForm,
    validateOnChange: true,
    validateOnMount: true,
  });

  const hasErrors = useMemo(() => Object.keys(formik.errors).length > 0, [formik.errors]);
  const isFachinteressentAccount = isFachinteressent(installer);

  if (isEditor) {
    return <RenderAemComponent componentProps={{}} name="DebDeleteCompanyForm" />;
  }
  return (
    <div className="deb-delete-company-form">
      <div className="deb-delete-company-form__container">
        {formik.values ? (
          <form
            className="deb-delete-company__container"
            onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}>
            <PageTitle text="Firmeneintrag löschen"> </PageTitle>
            {isFachinteressentAccount ? (
              <div>Hier können Sie die Löschung Ihres Portalzugangs beantragen.</div>
            ) : (
              <>
                <div>
                  Ihre Daten im Installateurverzeichnis werden nach der gesetzlichen Aufbewahrungsfrist
                  endgültig aus dem System gelöscht.
                </div>
                <br />
                <div className="deb-delete-company__hero">
                  Sobald Ihr Eintrag im Installateurverzeichnis gelöscht wurde, haben Sie keinen Zugriff
                  mehr auf das Informationsportal e-fix (
                  <a href="https://www.e-fix.info/">https://www.e-fix.info/</a>) und das
                  meinAuftragsportal (mAP) ebenso wie die Systeme der Schwesterunternehmen.
                  <br />
                  Das beinhaltet Zugang zu den folgenden Portalen der Netzbetreiber:
                </div>
                <div className="deb-delete-company__table">
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">Avacon Netz GmbH:</div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.avacon-netz.de/de/meinauftragsportal.html">
                        https://www.avacon-netz.de/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">Bayernwerk Netz GmbH: </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.bayernwerk-netz.de/de/meinauftragsportal.html">
                        https://www.bayernwerk-netz.de/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">E.DIS Netz GmbH: </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.e-dis-netz.de/de/meinauftragsportal.html">
                        https://www.e-dis-netz.de/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">ElbEnergie GmbH: </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.elbenergie.com/de/meinauftragsportal.html">
                        https://www.elbenergie.com/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">HanseGas GmbH: </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.hansegas.com/de/meinauftragsportal.html">
                        https://www.hansegas.com/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">NordNetz GmbH: </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.sh-netz.com/de/meinauftragsportal.html">
                        https://www.sh-netz.com/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">
                      Schleswig-Holstein Netz GmbH:
                    </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://www.sh-netz.com/de/meinauftragsportal.html">
                        https://www.sh-netz.com/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">LEW Verteilnetz GmbH:</div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://portal.lew-verteilnetz.de/de/meinauftragsportal.html">
                        https://portal.lew-verteilnetz.de/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                  <div className="deb-delete-company__table__row">
                    <div className="deb-delete-company__table__row__item">
                      Mitteldeutsche Netzgesellschaft Strom mbH: <br /> Mitteldeutsche Netzgesellschaft
                      Gas mbH: <br /> Verteilnetz Plauen GmbH:
                    </div>
                    <div className="deb-delete-company__table__row__item__link">
                      <a href="https://portal.mitnetz-strom.de/de/meinauftragsportal.html">
                        https://portal.mitnetz-strom.de/de/meinauftragsportal.html
                      </a>
                    </div>
                  </div>
                </div>
              </>
            )}
            <br />
            <div className="deb-delete-company__inputs">
              <CheckboxRow
                name="deletedCheckbox"
                onChange={formik.handleChange}
                value={formik.values.deletedCheckbox}
                error={Boolean(formik.errors.deletedCheckbox)}
                oldValue="false"
                helperText={formik.errors.deletedCheckbox || ''}
                label={
                  isFachinteressentAccount
                    ? 'Ich möchte die Löschung meines Portalzugangs beantragen'
                    : 'Ich möchte meinen Eintrag in den oben genannten Verzeichnissen löschen'
                }
              />
              <InputField
                label="Grund der Löschung"
                name="deleteReason"
                oldValue=""
                error={Boolean(formik.errors.deleteReason)}
                helperText={formik.errors.deleteReason || ''}
                onChange={formik.handleChange}
                value={formik.values.deleteReason}
                fullWidth
              />
              <InputField
                oldValue=""
                label="Löschung gültig ab"
                error={Boolean(formik.errors.deleteDate)}
                helperText={formik.errors.deleteDate || ''}
                value={formik.values.deleteDate}
                name="deleteDate"
                onChange={formik.handleChange}
                fullWidth
              />
            </div>
            <ActionButton disabled={hasErrors} inverted title="Firma löschen" type="submit" />
          </form>
        ) : (
          <LoadingIndicator />
        )}
      </div>
      <SubmitPopup
        type="DELETE_COMPANY"
        loading={isSubmitting}
        popupOpen={openPopup}
        setPopupOpen={() => setOpenPopup(false)}
        response={response}
        redirectLinkOnSuccessOrCancel={getMyDataPath()}
        onErrorRetry={() => {
          submitForm();
        }}
      />
    </div>
  );
};

DebDeleteCompanyForm.displayName = 'DebDeleteCompanyForm';

DebDeleteCompanyForm.propTypes = {};

DebDeleteCompanyForm.defaultProps = {};

MapTo(['e-fix/components/content/deleteCompany'])(DebDeleteCompanyForm);
